import { useMemo } from "react";
import { useInsuranceRiskTypesQuery } from "web_myplann/Api/Portfolio/Queries/useInsuranceRiskTypesQuery";
import {
	ClientInsuredPersonRiskVariantEnum,
	ClientPortfolioInsuredPersonRiskEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { FlatRowSelectOption } from "web_myplann/Shared/Components/FlatRow/FlatRowSelect";

const RISK_VALUES = Object.values(ClientPortfolioInsuredPersonRiskEnum);
const VARIANT_VALUES = Object.values(ClientInsuredPersonRiskVariantEnum);

const isRisk = (value: string): value is ClientPortfolioInsuredPersonRiskEnum =>
	RISK_VALUES.includes(value as ClientPortfolioInsuredPersonRiskEnum);

const isVariant = (
	value: string,
): value is ClientInsuredPersonRiskVariantEnum =>
	VARIANT_VALUES.includes(value as ClientInsuredPersonRiskVariantEnum);

export const useInsuranceRiskOptions = () => {
	const { data } = useInsuranceRiskTypesQuery();

	return useMemo(() => {
		const risks = data?.risks ?? [];
		const variantNames = new Map(
			(data?.variants ?? []).map((variant) => [variant.id, variant.name]),
		);

		const riskOptions: FlatRowSelectOption<ClientPortfolioInsuredPersonRiskEnum>[] =
			risks.flatMap((risk) =>
				isRisk(risk.id) ? [{ value: risk.id, label: risk.name }] : [],
			);

		const getVariantOptions = (
			risk: ClientPortfolioInsuredPersonRiskEnum | null,
		): FlatRowSelectOption<ClientInsuredPersonRiskVariantEnum>[] => {
			const allowedVariants =
				risk === null
					? (data?.variants ?? []).map((variant) => variant.id)
					: Object.keys(risks.find((item) => item.id === risk)?.variants ?? {});

			return allowedVariants.flatMap((variant) =>
				isVariant(variant)
					? [{ value: variant, label: variantNames.get(variant) ?? variant }]
					: [],
			);
		};

		return { riskOptions, getVariantOptions };
	}, [data]);
};
